import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Badge, Card, Modal, Select, Textarea } from "../components/ui";
import AllegatiGallery from "../components/richiesteIntervento/AllegatiGallery";
import ConversazioneEmail from "../components/richiesteIntervento/ConversazioneEmail";
import {
  STATO,
  TIPOLOGIA_RICHIESTA,
  ESITO,
  ESITO_OPTIONS,
  statoMeta,
} from "../constants/richiesteIntervento";

function Row({ label, children }) {
  return (
    <div className="flex py-2 border-b border-gray-100">
      <div className="w-56 font-medium text-gray-500">{label}</div>
      <div className="flex-1 text-gray-900">{children ?? "—"}</div>
    </div>
  );
}

function RichiestaDetailPage() {
  const { id } = useParams();
  const { apiRequest, canPerformAction, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [richiesta, setRichiesta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const [showConcludi, setShowConcludi] = useState(false);
  const [concludiForm, setConcludiForm] = useState({ esito: "", note: "" });

  const canApprova = canPerformAction("richieste.approva");
  const canGestisci = canPerformAction("richieste.gestisci");
  const isAdmin = user?.ruolo === 100000000;
  const canRevisionare = isAdmin || canApprova || canGestisci;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest(`/richieste-intervento/${id}`);
      setRichiesta(res);
    } catch (err) {
      showToast(err.message || "Errore nel caricamento", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, id, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("it-IT") : "—");

  const doStato = async (stato) => {
    setActing(true);
    try {
      await apiRequest(`/richieste-intervento/${id}/stato`, {
        method: "PATCH",
        body: JSON.stringify({ stato }),
      });
      showToast("Stato aggiornato", "success");
      await load();
    } catch (err) {
      showToast(err.message || "Errore nell'aggiornamento", "error");
    } finally {
      setActing(false);
    }
  };

  const doConcludi = async () => {
    setActing(true);
    try {
      await apiRequest(`/richieste-intervento/${id}/concludi`, {
        method: "PATCH",
        body: JSON.stringify({
          esito:
            concludiForm.esito !== "" ? Number(concludiForm.esito) : undefined,
          noteConclusione: concludiForm.note || undefined,
        }),
      });
      showToast("Richiesta conclusa", "success");
      setShowConcludi(false);
      await load();
    } catch (err) {
      showToast(err.message || "Errore nella conclusione", "error");
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Caricamento…</div>;
  if (!richiesta)
    return <div className="p-6 text-gray-500">Richiesta non trovata.</div>;

  const meta = statoMeta(richiesta.stato);
  const stato = richiesta.stato;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate("/richieste-intervento")}
        className="text-sm text-gray-500 hover:underline mb-4"
      >
        ← Torna alle richieste
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {richiesta.oggetto}
          </h1>
          <div className="mt-2">
            <Badge variant={meta.variant}>{meta.label}</Badge>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {stato === STATO.IN_ATTESA && canRevisionare && (
            <Button
              onClick={() =>
                navigate(`/richieste-intervento/${richiesta.id}/revisione`)
              }
            >
              Revisiona
            </Button>
          )}
          {stato === STATO.APPROVATA && canGestisci && (
            <Button
              variant="outline"
              onClick={() => doStato(STATO.IN_LAVORAZIONE)}
              loading={acting}
            >
              Segna in lavorazione
            </Button>
          )}
          {(stato === STATO.APPROVATA || stato === STATO.IN_LAVORAZIONE) &&
            canGestisci && (
              <>
                <Button
                  onClick={() =>
                    navigate(`/interventi/nuovo?richiestaId=${richiesta.id}`)
                  }
                >
                  Registra intervento
                </Button>
                <Button variant="outline" onClick={() => setShowConcludi(true)}>
                  Concludi direttamente
                </Button>
              </>
            )}
        </div>
      </div>

      <Card className="mb-4">
        <h2 className="font-semibold text-gray-900 mb-2">Dettagli richiesta</h2>
        <Row label="RSA">{richiesta.rsa?.nome}</Row>
        <Row label="Tipologia richiesta">
          {TIPOLOGIA_RICHIESTA[richiesta.tipologiaRichiesta]}
        </Row>
        <Row label="Fornitore">{richiesta.fornitore?.nome}</Row>
        <Row label="Tipologia intervento">
          {richiesta.tipologiaIntervento?.nome}
        </Row>
        <Row label="Data richiesta esecuzione">
          {fmtDate(richiesta.dataRichiestaEsecuzione)}
        </Row>
        <Row label="Referente da contattare">
          {richiesta.referente
            ? `${richiesta.referente.nomeCompleto}${
                richiesta.referente.email
                  ? ` — ${richiesta.referente.email}`
                  : richiesta.referente.contatto
                    ? ` — ${richiesta.referente.contatto}`
                    : ""
              }`
            : "—"}
        </Row>
        <Row label="Descrizione Intervento">{richiesta.note}</Row>
      </Card>

      <AllegatiGallery
        richiestaId={richiesta.id}
        allegati={richiesta.allegati}
      />

      {canGestisci && stato !== STATO.IN_ATTESA && (
        <ConversazioneEmail richiestaId={richiesta.id} />
      )}

      <Card className="mb-4">
        <h2 className="font-semibold text-gray-900 mb-2">Workflow</h2>
        <Row label="Richiedente">{richiesta.richiedente}</Row>
        <Row label="Data inserimento">
          {fmtDateTime(richiesta.dataInserimento)}
        </Row>
        <Row label="Approvato/Rifiutato da">{richiesta.approvatoDa}</Row>
        <Row label="Data approvazione">
          {fmtDateTime(richiesta.dataApprovazione)}
        </Row>
        {stato === STATO.RIFIUTATA && (
          <Row label="Motivo rifiuto">{richiesta.motivoRifiuto}</Row>
        )}
        {stato === STATO.CONCLUSA && (
          <>
            <Row label="Esito">{ESITO[richiesta.esito] || "—"}</Row>
            <Row label="Note conclusione">{richiesta.noteConclusione}</Row>
            <Row label="Data conclusione">
              {fmtDateTime(richiesta.dataConclusione)}
            </Row>
            <Row label="Intervento collegato">
              {richiesta.intervento ? (
                <button
                  className="text-primary-600 hover:underline"
                  onClick={() =>
                    navigate(`/interventi/${richiesta.intervento.id}`)
                  }
                >
                  {richiesta.intervento.nome || "Vai all'intervento"}
                </button>
              ) : (
                "—"
              )}
            </Row>
          </>
        )}
      </Card>

      <Modal
        isOpen={showConcludi}
        onClose={() => setShowConcludi(false)}
        title="Concludi richiesta"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowConcludi(false)}>
              Annulla
            </Button>
            <Button onClick={doConcludi} loading={acting}>
              Concludi
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Conclusione diretta senza registrare un intervento dal portale (es.
            richiesta evasa fuori sistema).
          </p>
          <Select
            label="Esito"
            value={concludiForm.esito}
            onChange={(e) =>
              setConcludiForm((f) => ({ ...f, esito: e.target.value }))
            }
          >
            <option value="">Seleziona esito…</option>
            {ESITO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Textarea
            label="Note conclusione"
            value={concludiForm.note}
            onChange={(e) =>
              setConcludiForm((f) => ({ ...f, note: e.target.value }))
            }
            rows={3}
          />
        </div>
      </Modal>
    </div>
  );
}

export default RichiestaDetailPage;
